(() => {
  if (window.KultfrauDiscoveryAnalytics) return;
  const endpoint = 'https://analyse.kultfrau.de/entdecken-analytics/api/events';
  const types = new Set(['discovery_opened', 'product_viewed', 'decision', 'decision_undone', 'wishlist_changed', 'detail_opened', 'cart_added']);
  const text = (value, limit = 200) => String(value ?? '').replace(/[\u0000-\u001f]/g, '').slice(0, limit);
  const productImage = value => {
    try {
      const url = new URL(value, 'https://kultfrau.de');
      const cdn = url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/');
      const shopCdn = ['kultfrau.de', 'www.kultfrau.de'].includes(url.hostname) && url.pathname.startsWith('/cdn/shop/files/');
      if (url.protocol !== 'https:' || url.username || url.password || url.port || (!cdn && !shopCdn)) return '';
      const version = url.searchParams.get('v');
      url.search = '';
      if (version && /^\d+$/.test(version)) url.searchParams.set('v', version);
      url.searchParams.set('width', '320');
      url.hash = '';
      return url.href.length <= 800 ? url.href : '';
    } catch { return ''; }
  };
  class DiscoveryAnalytics {
    constructor(owner) {
      this.owner = owner;
      this.queue = [];
      this.viewed = new Set();
      this.allowed = false;
      this.controller = new AbortController();
      const on = (target, name, handler) => target.addEventListener(name, handler, {signal: this.controller.signal});
      on(document, 'visitorConsentCollected', event => {
        this.consentDenied = event.detail?.analyticsAllowed === false;
        this.syncConsent();
        this.view();
      });
      on(document, 'visibilitychange', () => {
        this.syncConsent();
        if (document.visibilityState === 'hidden') void this.flush();
        else this.view();
      });
      on(window, 'pagehide', () => { void this.flush(); });
      on(window, 'pageshow', () => { this.syncConsent(); this.view(); });
      on(window, 'resize', () => this.view());
      on(owner.photo, 'load', () => this.view());
      if (window.IntersectionObserver) {
        this.observer = new IntersectionObserver(() => this.view(), {threshold: [.5]});
        this.observer.observe(owner.card);
      }
      // Shopify documents this API and event at shopify.dev/docs/api/customer-privacy.
      this.syncConsent();
      if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
        try {
          window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], error => {
            if (!error && !this.disposed) { this.syncConsent(); this.view(); }
          });
        } catch { /* An unavailable consent API never blocks discovery. */ }
      }
    }
    permission() {
      try { return !this.consentDenied && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
      catch { return false; }
    }
    syncConsent() {
      if (this.disposed) return false;
      const allowed = this.permission();
      if (!allowed) {
        if (this.allowed || this.queue.length || this.flight) this.clear();
        return false;
      }
      if (!this.allowed) {
        // Identifiers exist only in memory while analytics processing is allowed.
        if (!window.crypto?.randomUUID) return false;
        this.allowed = true;
        this.sessionId = window.crypto.randomUUID();
        this.pageId = window.crypto.randomUUID();
        this.enqueue('discovery_opened', null, {});
      }
      return true;
    }
    context() { return this.syncConsent() ? this.sessionId : null; }
    record(type, product, fields = {}, context) {
      try {
        if (!types.has(type) || !this.syncConsent() || (context !== undefined && context !== this.sessionId)) return null;
        return this.enqueue(type, product, fields);
      } catch { return null; }
    }
    enqueue(type, product, fields) {
      if (this.queue.length >= 100) return null;
      const data = this.owner.dataset;
      const event = {
        schema_version: 1, source: 'entdecken', event_id: window.crypto.randomUUID(),
        session_id: this.sessionId, page_id: this.pageId, occurred_at: new Date().toISOString(),
        event_type: type, environment: data.themeRole === 'main' ? 'live' : 'preview',
        theme_id: text(data.themeId, 30), locale: text(data.locale, 20), collection: text(data.collectionHandle, 150)
      };
      if (product?.id) Object.assign(event, {
        product_id: text(product.id, 30), product_title: text(product.title), product_handle: text(product.handle, 150),
        product_image: productImage(product.images?.[0]?.small || product.images?.[0]?.src)
      });
      for (const key of ['kind', 'input', 'original_event_id', 'operation', 'origin', 'variant_id']) {
        if (typeof fields[key] === 'string') event[key] = text(fields[key], 64);
      }
      if (typeof fields.saved_new === 'boolean') event.saved_new = fields.saved_new;
      if (fields.quantity === 1) event.quantity = 1;
      this.queue.push({event, attempts: 0});
      this.schedule(600);
      return event.event_id;
    }
    schedule(delay) {
      if (this.timer || this.flight || this.disposed || !this.queue.length) return;
      this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, delay);
    }
    async flush() {
      if (!this.syncConsent() || this.flight || !this.queue.length) return;
      clearTimeout(this.timer); this.timer = null;
      const entries = this.queue.slice(0, 20);
      const flight = {controller: new AbortController(), sessionId: this.sessionId};
      this.flight = flight;
      let retry = false;
      const timeout = setTimeout(() => flight.controller.abort(), 10000);
      try {
        const response = await fetch(endpoint, {
          method: 'POST', mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer', keepalive: true,
          headers: {'Content-Type': 'application/json'}, body: JSON.stringify({events: entries.map(entry => entry.event)}),
          signal: flight.controller.signal
        });
        retry = !response.ok && (response.status === 408 || response.status === 429 || response.status >= 500);
      } catch { retry = true; }
      finally {
        clearTimeout(timeout);
        if (this.flight === flight) {
          this.flight = null;
          if (!this.syncConsent() || flight.sessionId !== this.sessionId) return;
          const completed = new Set(entries.filter(entry => !retry || ++entry.attempts >= 3).map(entry => entry.event.event_id));
          this.queue = this.queue.filter(entry => !completed.has(entry.event.event_id));
          this.schedule(retry ? 3000 : 300);
        }
      }
    }
    view() {
      try {
        if (!this.syncConsent()) return;
        const {current, imageIndex, photo} = this.owner;
        if (!current || this.viewed.has(String(current.id))) return;
        const context = this.sessionId, key = `${current.id}:${imageIndex}:${photo.src}`;
        if (this.decodedKey === key) { this.showView(current, key, context); return; }
        if (this.decodeKey === key) return;
        this.decodeKey = key;
        Promise.resolve(photo.decode()).then(() => {
          if (this.decodeKey !== key || context !== this.sessionId) return;
          this.decodedKey = key;
          this.showView(current, key, context);
        }, () => {}).finally(() => { if (this.decodeKey === key) this.decodeKey = null; });
      } catch {}
    }
    showView(product, key, context) {
      if (this.viewFrame) cancelAnimationFrame(this.viewFrame);
      this.viewFrame = requestAnimationFrame(() => {
        this.viewFrame = null;
        try {
          const {current, imageIndex, photo, card, busy} = this.owner;
          if (!this.syncConsent() || context !== this.sessionId || !current || this.viewed.has(String(product.id))) return;
          if (`${current.id}:${imageIndex}:${photo.src}` !== key || busy || this.owner.isConnected === false || document.visibilityState === 'hidden') return;
          if (!photo.complete || !photo.naturalWidth || card.hidden || photo.hidden) return;
          const cardStyle = getComputedStyle(card), photoStyle = getComputedStyle(photo);
          if ([cardStyle, photoStyle].some(style => style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0)) return;
          const rect = card.getBoundingClientRect();
          const visible = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0)) * Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
          if (!rect.width || !rect.height || visible / (rect.width * rect.height) < .5) return;
          if (this.record('product_viewed', product, {}, context)) this.viewed.add(String(product.id));
        } catch {}
      });
    }
    clear() {
      this.allowed = false;
      this.queue = [];
      this.viewed.clear();
      this.sessionId = null; this.pageId = null; this.decodeKey = null; this.decodedKey = null;
      clearTimeout(this.timer); this.timer = null;
      if (this.viewFrame) cancelAnimationFrame(this.viewFrame);
      this.viewFrame = null;
      this.flight?.controller.abort(); this.flight = null;
      this.owner.forgetAnalytics?.();
    }
    destroy() {
      this.clear(); this.disposed = true;
      this.controller.abort(); this.observer?.disconnect();
    }
  }
  window.KultfrauDiscoveryAnalytics = {create(owner) { try { return new DiscoveryAnalytics(owner); } catch { return null; } }};
})();
